/**
 * SkyEnvironmentBaker — produces a PMREM-prefiltered IBL cube from the sky.
 *
 * Drives `scene.environment` so PBR materials receive physically-plausible
 * indirect diffuse + specular from the atmosphere. The sky material (a
 * NodeMaterial fed by `SkyRenderer.buildSkyMaterial()`) is rendered into a
 * small CubeRenderTarget each time the sun direction changes by more than
 * a threshold, then PMREM'd. On mid-range GPUs this costs ~1–3 ms per bake,
 * so triggers are throttled (see SkyModule).
 *
 * Lifecycle:
 *   bake(scene) — performs one render + PMREM cycle. Safe to call per frame
 *                 but the module only calls it when the sun has moved or
 *                 atmosphere config changed.
 */

import * as THREE from 'three/webgpu';
import type { SkyRenderer } from './SkyRenderer.js';
import { Logger } from '../../core/Logger.js';

export interface SkyEnvironmentBakerOptions {
  renderer: THREE.WebGPURenderer;
  skyRenderer: SkyRenderer;
  /** Cube face resolution. 64/128/256. Default 128. */
  resolution?: number;
}

export class SkyEnvironmentBaker {
  private _renderer: THREE.WebGPURenderer;
  private _skyRenderer: SkyRenderer;
  private _resolution: number;
  private _cubeRT: THREE.CubeRenderTarget | null = null;
  // RenderTarget parent type — PMREMGenerator.fromCubemap() returns the
  // abstract RenderTarget in Three.js r182 typings.
  private _pmremRT: THREE.RenderTarget | null = null;
  private _cubeCamera: THREE.CubeCamera | null = null;
  private _skyScene: THREE.Scene | null = null;
  private _skyMesh: THREE.Mesh | null = null;
  private _pmrem: THREE.PMREMGenerator | null = null;
  private _output: THREE.Texture | null = null;
  private _disposed = false;

  constructor(opts: SkyEnvironmentBakerOptions) {
    this._renderer = opts.renderer;
    this._skyRenderer = opts.skyRenderer;
    this._resolution = opts.resolution ?? 128;
  }

  /**
   * The current PMREM-prefiltered environment texture, or null if bake()
   * has not been called yet.
   */
  get output(): THREE.Texture | null {
    return this._output;
  }

  /** Amortized-bake cursor: 0..5 = cube faces, 6 = PMREM base, 7 = GGX levels. */
  private _step = 0;
  /** Next GGX lod level while _step === 7. */
  private _ggxI = 1;

  /** Where the amortized cycle stands (0..5 faces, 6 the PMREM base, 7 the GGX levels; 0 = idle or about to latch). */
  get cycleStep(): number { return this._step; }

  /**
   * Abandon the amortized cycle in flight (2026-09-27, the latch law): the next bakeStep() starts again at face 0 and
   * re-latches the overrides. The faces rendered so far are simply overwritten; a cycle abandoned inside its PMREM
   * stage leaves the live target part-updated until the next cycle completes (one-shot bake() rewrites it whole).
   */
  resetCycle(): void {
    this._step = 0;
    this._ggxI = 1;
  }

  /**
   * One frame's worth of the amortized bake: renders a SINGLE cube face per
   * call (6 calls), then PMREM-prefilters into the persistent target on the
   * 7th. Live-profiled 2026-07-31: the one-shot bake() — 6 face renders + the
   * PMREM blur chain, ~30 small passes — was a recurring ~120 ms main-thread
   * tick every re-bake interval (no single pass was big; the ENCODe count
   * was). Spreading faces across frames leaves only the PMREM frame as a
   * hitch. Sun drift across the 7-frame window (~120 ms) is far below the
   * bake threshold — the faces stay mutually consistent.
   */
  bakeStep(): 'pending' | 'done' | 'failed' {
    if (this._disposed) return 'failed';
    this._ensureInitialized();
    const renderer = this._renderer;
    try {
      if (this._step < 6) {
        const cam = this._cubeCamera!;
        if (cam.parent === null) cam.updateMatrixWorld();
        // CubeCamera.update() re-orients its face cameras when the renderer's
        // coordinate system differs (WebGPU vs WebGL) — the per-face path
        // must mirror that or the faces render with WebGL orientation.
        const camCS = cam as unknown as { coordinateSystem: unknown; updateCoordinateSystem(): void };
        const renCS = renderer as unknown as { coordinateSystem: unknown };
        if (camCS.coordinateSystem !== renCS.coordinateSystem) {
          camCS.coordinateSystem = renCS.coordinateSystem;
          camCS.updateCoordinateSystem();
        }
        cam.position.set(0, 0, 0);
        const faceCam = cam.children[this._step] as THREE.PerspectiveCamera;
        const prevRT = renderer.getRenderTarget();
        const prevFace = renderer.getActiveCubeFace();
        const prevMip = renderer.getActiveMipmapLevel();
        const xrWas = renderer.xr.enabled;
        renderer.xr.enabled = false;
        // The bake's own sky values (no disc, the weather blend — SkyBakeOverrides),
        // latched at the cycle's first face so all six agree. Uniforms are read at
        // draw time, so the swap is scoped to this one render.
        if (this._step === 0) this._skyRenderer.latchBakeOverrides?.();
        this._skyRenderer.beginBake?.();
        try {
          renderer.setRenderTarget(this._cubeRT!, this._step);
          renderer.render(this._skyScene!, faceCam);
        } finally {
          this._skyRenderer.endBake?.();
        }
        renderer.setRenderTarget(prevRT, prevFace, prevMip);
        renderer.xr.enabled = xrWas;
        this._step++;
        return 'pending';
      }
      // ── Staged PMREM (live-profiled 2026-07-31: the prefilter chain alone
      // was the whole ~120-150 ms hitch — the faces above are nearly free).
      // r182's generator is cleanly separable: _textureToCubeUV writes the
      // base level, then each _applyGGXFilter(lodIn→lodOut) is independent
      // with all cross-call state living in the render targets. One level per
      // frame turns the hitch into ~15-20 ms slivers. autoClear must be OFF
      // for every staged sub-step: the one-shot path cleared the whole
      // persistent target at the base pass and rebuilt all lods same-frame —
      // spread across frames that clear would flash black mips (the env
      // texture is LIVE mid-bake).
      // XR guard for EVERY PMREM stage below, mirroring the face-render guard
      // above: while a session is presenting, an internal renderer.render()
      // with xr.enabled still true is hijacked by the XR camera/framebuffer
      // substitution and would write garbage into the LIVE HalfFloat env
      // target. (Hardening found while hunting the 2026-08-16 black-lighting
      // bug — whose actual root cause was the unclamped sub-ground altitude
      // in atmosphereFn.ts.)
      const xrWasPmrem = renderer.xr.enabled;
      renderer.xr.enabled = false;
      try {
        if (this._pmremRT === null) {
          // First bake: one-shot so scene.environment exists as early as ever.
          this._pmremRT = this._pmrem!.fromCubemap(this._cubeRT!.texture);
          this._output = this._pmremRT.texture;
          this._step = 0;
          this._skyRenderer.bakeCompleted?.();
          return 'done';
        }
        const g = this._pmrem as unknown as {
          _setSizeFromTexture(t: THREE.Texture): void;
          _init(rt: THREE.RenderTarget): void;
          _textureToCubeUV(t: THREE.Texture, rt: THREE.RenderTarget): void;
          _applyGGXFilter(rt: THREE.RenderTarget, lodIn: number, lodOut: number): void;
          _lodMeshes: unknown[];
          _pingPongRenderTarget: THREE.RenderTarget | null;
        };
        const rt = this._pmremRT;
        const prevRT2 = renderer.getRenderTarget();
        const prevFace2 = renderer.getActiveCubeFace();
        const prevMip2 = renderer.getActiveMipmapLevel();
        const prevAutoClear = renderer.autoClear;
        renderer.autoClear = false;
        try {
          if (this._step === 6) {
            g._setSizeFromTexture(this._cubeRT!.texture);
            g._init(rt);
            // Zero-clear the generator's internal ping-pong target once per
            // staged cycle. With autoClear OFF (required so the LIVE cubeUV
            // env is never wiped mid-bake) nothing ever clears it, and the
            // GGX copy-back pass can bilinearly touch ping-pong texels just
            // outside the region the blur pass wrote — memory WebGL leaves
            // UNDEFINED (WebGPU zero-inits). Cheap insurance against smearing
            // undefined halfs into the identity-stable env texture.
            const pp = g._pingPongRenderTarget;
            if (pp) {
              pp.scissorTest = false;
              pp.viewport.set(0, 0, pp.width, pp.height);
              pp.scissor.set(0, 0, pp.width, pp.height);
              // getClearColor wants three's internal Color4 (extends Color,
              // not exported) — cast the target arg only (FeedbackPass idiom).
              const prevCC = new THREE.Color();
              renderer.getClearColor(prevCC as unknown as Parameters<typeof renderer.getClearColor>[0]);
              const prevCA = renderer.getClearAlpha();
              renderer.setClearColor(0x000000, 0);
              renderer.setRenderTarget(pp);
              renderer.clear(true, false, false);
              renderer.setClearColor(prevCC, prevCA);
            }
            g._textureToCubeUV(this._cubeRT!.texture, rt);
            this._ggxI = 1;
            this._step = 7;
            return 'pending';
          }
          // _step === 7: one GGX importance-sampling level per call.
          g._applyGGXFilter(rt, this._ggxI - 1, this._ggxI);
          this._ggxI++;
          if (this._ggxI < g._lodMeshes.length) return 'pending';
          // Finished — replicate _cleanup's target-side reset (its renderer
          // restore uses module state captured only by the one-shot path).
          rt.scissorTest = false;
          rt.viewport.set(0, 0, rt.width, rt.height);
          rt.scissor.set(0, 0, rt.width, rt.height);
          this._step = 0;
          this._skyRenderer.bakeCompleted?.();
          return 'done';
        } finally {
          renderer.autoClear = prevAutoClear;
          renderer.setRenderTarget(prevRT2, prevFace2, prevMip2);
        }
      } finally {
        renderer.xr.enabled = xrWasPmrem;
      }
    } catch (err) {
      this._step = 0;
      Logger.warn('SkyEnvBaker', `Bake step failed: ${err}`);
      return 'failed';
    }
  }

  /**
   * Bake the sky into a cube, then PMREM-prefilter to get the IBL texture.
   * Returns true on success, false if the renderer is unbound or disposed.
   */
  bake(): boolean {
    if (this._disposed) return false;
    this._ensureInitialized();
    // A one-shot bake supersedes any amortized cycle in flight (its faces and latch are stale now) — 2026-09-27.
    this.resetCycle();

    const skyScene = this._skyScene!;
    const cubeCamera = this._cubeCamera!;

    // Same XR guard as bakeStep(): a bake while a session presents would
    // otherwise render through the XR camera substitution into the live env
    // target (NaN-poisoned IBL → every lit material black).
    const xrWas = this._renderer.xr.enabled;
    this._renderer.xr.enabled = false;
    try {
      cubeCamera.position.set(0, 0, 0);
      this._skyRenderer.latchBakeOverrides?.();
      this._skyRenderer.beginBake?.();
      try { cubeCamera.update(this._renderer, skyScene); } finally { this._skyRenderer.endBake?.(); }

      // Re-bake INTO the persistent PMREM target (r182 fromCubemap accepts an
      // existing renderTarget): the texture OBJECT stays identity-stable and
      // only its contents change. The old dispose-and-reallocate cycle handed
      // scene.environment a NEW texture object every bake, and TSL binds the
      // texture OBJECT at pipeline build time — so each sun-drift/interval
      // re-bake invalidated EVERY PBR pipeline in the scene (profiled as
      // recurring multi-second node-build + program-link freezes mid-play).
      if (this._pmremRT) {
        this._pmrem!.fromCubemap(this._cubeRT!.texture, this._pmremRT);
      } else {
        this._pmremRT = this._pmrem!.fromCubemap(this._cubeRT!.texture);
        this._output = this._pmremRT.texture;
      }
      this._skyRenderer.bakeCompleted?.();
      return true;
    } catch (err) {
      Logger.warn('SkyEnvBaker', `Bake failed: ${err}`);
      return false;
    } finally {
      this._renderer.xr.enabled = xrWas;
    }
  }

  dispose(): void {
    this._disposed = true;
    if (this._pmremRT) { this._pmremRT.dispose(); this._pmremRT = null; }
    this._output = null;
    if (this._cubeRT) { this._cubeRT.dispose(); this._cubeRT = null; }
    if (this._pmrem) { this._pmrem.dispose(); this._pmrem = null; }
    if (this._skyMesh) {
      (this._skyMesh.geometry as THREE.BufferGeometry).dispose();
      (this._skyMesh.material as THREE.Material).dispose();
    }
    this._skyMesh = null;
    this._skyScene = null;
    this._cubeCamera = null;
  }

  private _ensureInitialized(): void {
    if (this._skyScene) return;

    // Sky-only scene: one inverted sphere with the sky material.
    // Sized large enough that the near plane of the cube camera fits inside.
    this._skyScene = new THREE.Scene();
    const skyMat = this._skyRenderer.buildSkyMaterial();
    const skyGeo = new THREE.SphereGeometry(10, 32, 16);
    this._skyMesh = new THREE.Mesh(skyGeo, skyMat);
    // Ensure material renders behind everything (no depth write)
    this._skyScene.add(this._skyMesh);

    this._cubeRT = new THREE.CubeRenderTarget(this._resolution, {
      generateMipmaps: false,
      format: THREE.RGBAFormat,
      type: THREE.HalfFloatType,
      magFilter: THREE.LinearFilter,
      minFilter: THREE.LinearFilter,
    });
    this._cubeRT.texture.name = 'SkyEnvCube';
    this._cubeCamera = new THREE.CubeCamera(0.1, 100, this._cubeRT);

    this._pmrem = new THREE.PMREMGenerator(this._renderer);
    this._pmrem.compileCubemapShader?.();
  }
}
